import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Image as ImageIcon, Loader2, Video } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { imagesAPI } from '../../services/api';

const MediaManagement = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [newMedia, setNewMedia] = useState({ title: '', description: '', type: 'image' });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const response = await imagesAPI.getAll();
      setImages(Array.isArray(response.data) ? response.data : []);
      setVideos([]);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Erreur lors du chargement des médias');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !newMedia.title) {
      toast.error('Veuillez remplir tous les champs et sélectionner un fichier');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', newMedia.title);
      formData.append('description', newMedia.description);

      if (newMedia.type === 'image') {
        await imagesAPI.upload(formData.get('file'));
      } else {
        toast.error('Upload vidéo non pris en charge pour le moment');
        return;
      }

      toast.success('Média sauvegardé avec succès !');
      setNewMedia({ title: '', description: '', type: 'image' });
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      loadMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId, type) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce média ?')) return;

    try {
      if (type === 'image') {
        await imagesAPI.delete(mediaId);
      } else {
        toast.error('Suppression vidéo non prise en charge pour le moment');
        return;
      }
      toast.success('Média supprimé');
      loadMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <h2 className="text-2xl font-bold text-white">Gestion des Médias</h2>

      {/* Upload Form */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Ajouter un Média
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            value={newMedia.title}
            onChange={(e) => setNewMedia({ ...newMedia, title: e.target.value })}
            placeholder="Titre du média"
            className="bg-slate-700 border-slate-600 text-white"
          />

          <select
            value={newMedia.type}
            onChange={(e) => setNewMedia({ ...newMedia, type: e.target.value })}
            className="bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
          >
            <option value="image">Image</option>
            <option value="video">Vidéo</option>
          </select>
        </div>

        <textarea
          value={newMedia.description}
          onChange={(e) => setNewMedia({ ...newMedia, description: e.target.value })}
          placeholder="Description du média"
          className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-white min-h-[80px]"
        />

        <div className="flex items-center gap-4">
          <input
            type="file"
            onChange={(e) => {
              setSelectedFile(e.target.files[0]);
              console.log('Fichier sélectionné:', e.target.files[0]);
            }}
            className="text-gray-300"
            accept={newMedia.type === 'image' ? 'image/*' : 'video/*'}
          />
          <Button
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Upload en cours...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Uploader et Sauvegarder
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-gray-400 mt-2">
          ℹ️ Le fichier sera automatiquement sauvegardé après l'upload
        </p>
      </div>

      {/* Images Gallery */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Images ({images.length})
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div key={img.id} className="relative group">
              <img
                src={img.url}
                alt={img.title}
                className="w-full h-40 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(img.id, 'image')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-white text-sm mt-2 truncate">{img.title}</p>
            </div>
          ))}

          {images.length === 0 && (
            <p className="text-gray-400 col-span-full text-center py-8">Aucune image disponible</p>
          )}
        </div>
      </div>

      {/* Videos Gallery */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Video className="w-5 h-5" />
          Vidéos ({videos.length})
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {videos.map((video) => (
            <div key={video.id} className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-medium">{video.title}</h4>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(video.id, 'video')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-gray-400 text-sm">{video.description}</p>
              {video.url && (
                <video
                  src={video.url}
                  controls
                  className="w-full mt-3 rounded"
                />
              )}
            </div>
          ))}

          {videos.length === 0 && (
            <p className="text-gray-400 col-span-full text-center py-8">Aucune vidéo disponible</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaManagement;
